// @flow

import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { compose } from "redux";
import { localize } from "redux-i18n";
import config from "@config";
import { formatDate } from "@transformers/date";
import { AddEditStaffGroupModal, StaffGroupsList } from "@modules/service/components";
import * as serviceSelectors from "@modules/service/selectors";
import actionCreators from "@modules/service/actionCreator";
import { Button, View, Pagination, Loader, GSGrid } from "@components";
import { toTableSortV2 } from "@transformers/sort";
import { camelCaseToUnderscore } from "@transformers/string";
import {
  UnassignReassignShiftPatternModal,
  ShiftPatternDeleteModal,
  ShiftPatternAddModal,
} from "@modules/service/components";
import type {
  NewShiftPattern,
  ShiftPattern,
  UnassignReassignShiftPatternModalType,
  EmployeePositionsAssignedToShiftPattern,
  DepartmentsAssignedToShiftPattern,
} from "@modules/service/reducer/shift-patterns/types";
import type { PaginationType } from "@types";

import type { State } from "@store/reducers";

type OwnProps = $Exact<{
  clientId: number,
}>;

type PropsFromState = $Exact<{
  list: Array<ShiftPattern>,
  totalList?: Array<ShiftPattern>,
  listPagination: PaginationType,
  data: ?ShiftPattern,
  showAddModal: boolean,
  showEditShiftPatternModal: boolean,
  showDeleteModal: boolean,
  isUnassignReassignShiftPatternModalVisible: boolean,
  selectedShiftPattern: ?ShiftPattern,
  unassignReassignShiftPatternModalType: ?UnassignReassignShiftPatternModalType,
  employeePositionsAssignedToShiftPattern: ?Array<EmployeePositionsAssignedToShiftPattern>,
  departmentsAssignedToShiftPattern: ?Array<DepartmentsAssignedToShiftPattern>,
  isReassignItemsToShiftPatternModalVisible: boolean,
  totalElements: number,
  isLoading: boolean,
}>;

type PropsFromDispatch = $Exact<{
  getShiftPatternList: typeof actionCreators.GetShiftPatterns,
  setNewShiftPattern: typeof actionCreators.NewShiftPattern,
  setShiftPattern: typeof actionCreators.SetShiftPattern,
  setDraft: typeof actionCreators.UpdateShiftPatternDraft,
  setShowAddModal: typeof actionCreators.ShowAddShiftPatternModal,
  setShowEditShiftPatternModal: typeof actionCreators.ShowEditShiftPatternModal,
  setShowDeleteModal: typeof actionCreators.ShowDeleteShiftPatternModal,
  showEmployeesForPatternModal: typeof actionCreators.GetEmployeePositionsAssignedToShiftPattern,
  showDepartmentsForPatternModal: typeof actionCreators.GetDepartmentsAssignedToShiftPattern,
  toggleUnassignReassignShiftPatternModal: typeof actionCreators.ToggleUnassignReassignShiftPatternModal,
  toggleUnassignReassignItemsToShiftPatternModal: typeof actionCreators.ToggleUnassignReassignItemsToShiftPatternModal,
  unassignReassignItemsShiftPattern: typeof actionCreators.UnassignReassignItemsShiftPattern,
  getTotalList: typeof actionCreators.GetTotalShiftPatterns,
}>;

type Props = { ...OwnProps, ...PropsFromState, ...PropsFromDispatch, ...PropsFromLocalize };

export function ShiftPatterns(props: Props) {
  useEffect(() => {
    props.getShiftPatternList({
      clientId: props.clientId,
    });
  }, [props.clientId]);

  const columnsShiftPattern = [
    {
      name: props.t("ID"),
      key: "id",
      width: 50,
    },
    {
      name: props.t("SHIFT_PATTERN"),
      key: "name",
      align: "left",
      sortable: true,
    },
    {
      name: props.t("DURATION"),
      key: "duration",
      renderCell: ({ row }: { row: ShiftPattern }) => `${row.shiftPatternDays.length} days`,
      align: "left",
    },
    {
      name: props.t("DATE_ADDED"),
      key: "createdAt",
      renderCell: ({ row }: { row: ShiftPattern }) => formatDate(row.createdAt),
      sortable: true,
    },
    {
      name: props.t("ADDED_BY"),
      key: "addedBy",
      renderCell: ({ row }: { row: ShiftPattern }) =>
        row.createdBy ? `${row.createdBy.firstName} ${row.createdBy.lastName}` : null,
      align: "left",
    },
    {
      name: props.t("SHIFT_PATTERN_EMPLOYEES"),
      key: "employeeCount",
      onCellClick: onShowEmployeesForPatternModal,
      renderCell: ({ row }: { row: ShiftPattern }) => (
        <div className="d-flex align-items-center text-primary">
          <span className="material-icons pointer mr-2">person</span>
          <div className="text-charcoal-dark">{row.employeeCount}</div>
        </div>
      ),
    },
    {
      name: props.t("SHIFT_PATTERN_DEPARTMENTS"),
      key: "organisationalUnitCount",
      onCellClick: onShowDepartmentsForPatternModal,
      renderCell: ({ row }: { row: ShiftPattern }) => (
        <div className="d-flex align-items-center">
          <span className="material-icons pointer mr-2">folder</span>
          <div className="text-charcoal-dark">{row.organisationalUnitCount}</div>
        </div>
      ),
    },
    {
      name: "ACTION_MENU",
      contextMenuConfig: (row: ShiftPattern) => [
        {
          label: props.t("EDIT"),
          icon: "edit",
          onClick: handleShowEditShiftPattern(row),
        },
        {
          label: props.t("DELETE"),
          icon: "delete",
          onClick: handleDeleteShiftPattern(row),
        },
      ],
    },
  ];

  const handleShowAdd = () => {
    props.setNewShiftPattern();
    props.setShowAddModal(true);
  };

  function handleShowEditShiftPattern(item: ShiftPattern) {
    return function() {
      props.setDraft(item);
      props.setShowEditShiftPatternModal(true);
    };
  }

  function handleDeleteShiftPattern(item: ShiftPattern) {
    return function() {
      props.setShiftPattern(item);
      props.setShowDeleteModal(true);
    };
  }

  const onSort = (params: { id: string | null, direction: "asc" | "desc" }) => {
    props.getShiftPatternList({
      clientId: props.clientId,
      pageable: {
        sort: `${params.id ? camelCaseToUnderscore(params.id) : ""},${params.direction || ""}`,
      },
    });
  };

  const closeUnassignReassignShiftPatternModal = () => {
    props.toggleUnassignReassignShiftPatternModal({ visible: false });
  };

  function onShowEmployeesForPatternModal(shiftPattern: ShiftPattern) {
    props.showEmployeesForPatternModal({
      shiftPattern,
      clientId: props.clientId,
      modalVisible: true,
    });
    props.getTotalList({
      clientId: props.clientId,
      limit: props.totalElements,
    });
  }

  function onShowDepartmentsForPatternModal(shiftPattern: ShiftPattern) {
    props.getTotalList({
      clientId: props.clientId,
      limit: props.totalElements,
    });
    props.showDepartmentsForPatternModal({ shiftPattern, clientId: props.clientId });
  }

  const getSort = () => {
    const orders = (((props.listPagination || {}).sort || {}).orders || [])[0] || false;
    if (orders) {
      return [
        {
          property: orders.property,
          direction: orders.direction === "DESC" ? "desc" : "asc",
        },
      ];
    }
    return [{ property: "", direction: "desc" }];
  };

  const changePagination = (page: number) => {
    props.getShiftPatternList({
      clientId: props.clientId,
      pageable: {
        page,
      },
    });
  };

  const getTotalPages = () => {
    return Math.ceil((props.totalElements || 0) / props.listPagination.pageSize);
  };

  return (
    <View>
      <View className="message-box" margin={[0, 0, "1rem", 0]}>
        {props.t("SHIFT_PATTERNS_INFORMATION")}
      </View>
      <View className="gs-container">
        {props.isLoading && <Loader inline />}
        <View direction="row" margin={[0, 0, "1rem", 0]} justifyContent="end">
          <Button materialIcon="add_circle" onClick={handleShowAdd}>
            {props.t("ADD")}
          </Button>
        </View>
        <GSGrid
          rows={props.list}
          columns={columnsShiftPattern}
          sortColumns={toTableSortV2(getSort())}
          onSortChange={onSort}
        />
        {getTotalPages() > 1 && (
          <Pagination
            className="my-3 d-flex flex-fill justify-content-center"
            currentPage={props.listPagination.pageNumber}
            totalPages={getTotalPages()}
            onChange={changePagination}
          />
        )}
      </View>
      {/* Modals start */}
      {props.isUnassignReassignShiftPatternModalVisible && (
        <UnassignReassignShiftPatternModal
          isOpen={true}
          closeModal={closeUnassignReassignShiftPatternModal}
          clientId={props.clientId}
          shiftPattern={props.selectedShiftPattern}
          modalType={props.unassignReassignShiftPatternModalType}
          employees={props.employeePositionsAssignedToShiftPattern}
          departments={props.departmentsAssignedToShiftPattern}
          isReassignItemsToShiftPatternModalVisible={
            props.isReassignItemsToShiftPatternModalVisible
          }
          toggleUnassignReassignItemsToShiftPatternModal={
            props.toggleUnassignReassignItemsToShiftPatternModal
          }
          unassignReassignItemsShiftPattern={props.unassignReassignItemsShiftPattern}
          shiftPatternsList={props.totalList}
        />
      )}
      {(props.showAddModal || props.showEditShiftPatternModal) && (
        <ShiftPatternAddModal
          defaultStep={props.showAddModal ? 1 : 2}
          editing={props.showEditShiftPatternModal}
          clientId={props.clientId}
        />
      )}
      {props.showDeleteModal && !!props.data && (
        <ShiftPatternDeleteModal clientId={props.clientId} data={props.data} />
      )}
      {/* Modals end */}
    </View>
  );
}

const mapStateToProps = (state: State, ownProps: Object): PropsFromState => ({
  list: serviceSelectors.getShiftPatternsList(state),
  totalList: serviceSelectors.getTotalShiftPatternsList(state),
  listPagination: serviceSelectors.getShiftPatternsPagination(state),
  data: serviceSelectors.getCurrentShiftPattern(state),
  showAddModal: serviceSelectors.getShowAddShiftPatternModal(state),
  showEditShiftPatternModal: serviceSelectors.getShowEditShiftPatternModal(state),
  showDeleteModal: serviceSelectors.getShowDeleteShiftPatternModal(state),
  isUnassignReassignShiftPatternModalVisible: serviceSelectors.isUnassignReassignShiftPatternModalVisible(
    state,
  ),
  selectedShiftPattern: serviceSelectors.getSelectedShiftPattern(state),
  unassignReassignShiftPatternModalType: serviceSelectors.unassignReassignShiftPatternModalType(
    state,
  ),
  employeePositionsAssignedToShiftPattern: serviceSelectors.employeePositionsAssignedToShiftPattern(
    state,
  ),
  departmentsAssignedToShiftPattern: serviceSelectors.departmentsAssignedToShiftPattern(state),
  isReassignItemsToShiftPatternModalVisible: serviceSelectors.isReassignItemsToShiftPatternModalVisible(
    state,
  ),
  totalElements: serviceSelectors.getShiftPatternsTotalElements(state) || 0,
  isLoading: serviceSelectors.getShiftPatternsLoading(state),
});

const mapDispatchToProps = {
  getShiftPatternList: actionCreators.GetShiftPatterns,
  getTotalList: actionCreators.GetTotalShiftPatterns,
  setNewShiftPattern: actionCreators.NewShiftPattern,
  setShiftPattern: actionCreators.SetShiftPattern,
  setDraft: actionCreators.UpdateShiftPatternDraft,
  setShowAddModal: actionCreators.ShowAddShiftPatternModal,
  setShowEditShiftPatternModal: actionCreators.ShowEditShiftPatternModal,
  setShowDeleteModal: actionCreators.ShowDeleteShiftPatternModal,
  showEmployeesForPatternModal: actionCreators.GetEmployeePositionsAssignedToShiftPattern,
  showDepartmentsForPatternModal: actionCreators.GetDepartmentsAssignedToShiftPattern,
  toggleUnassignReassignShiftPatternModal: actionCreators.ToggleUnassignReassignShiftPatternModal,
  toggleUnassignReassignItemsToShiftPatternModal:
    actionCreators.ToggleUnassignReassignItemsToShiftPatternModal,
  unassignReassignItemsShiftPattern: actionCreators.UnassignReassignItemsShiftPattern,
};

export default compose(localize(), connect(mapStateToProps, mapDispatchToProps))(ShiftPatterns);
